import { Navigate, Route, Routes } from 'react-router-dom';
import { LoginPage } from '@/auth/LoginPage';
import { NoAccess, RequireAuth, RequirePermission } from '@/auth/guards';
import { useAuth } from '@/auth/useAuth';
import { OrderWorkspacePage } from '@/pages/OrderWorkspacePage';
import { JourneysPage } from '@/pages/JourneysPage';
import { PackagesPage } from '@/pages/PackagesPage';
import { TodayPage } from '@/pages/TodayPage';
import { FetchDomainsPage } from '@/pages/FetchDomainsPage';
import { PrintCountSheetPage } from '@/pages/PrintCountSheetPage';
import { WarehousePage } from '@/pages/WarehousePage';
import { WorkOverviewPage } from '@/pages/WorkOverviewPage';
import { WorkViewPage, type WorkViewId } from '@/pages/WorkViewPage';
import { FETCH_DOMAINS_PATH, VIEW_PERMISSIONS, firstAccessiblePath, routePath, viewCodes } from './navigation';
import { AppShell } from './AppShell';

/**
 * Real paths, per plan §8c — the hash `mailView` switch does not come along.
 *
 * NOTHING in this file names a permission code or a work view's path literal:
 * both come from `navigation.ts`, the single table the shell nav and the
 * overview also read (docs/PERMISSIONS.md). A route, its nav row and its
 * overview rows therefore cannot disagree about who may open it.
 */

/** One WORK route: the view, wrapped in the codes its nav row uses. */
function workRoute(view: WorkViewId) {
  return (
    <RequirePermission codes={viewCodes(view)}>
      <WorkViewPage view={view} />
    </RequirePermission>
  );
}

/**
 * `/` is the Overview for users who hold that page, and otherwise the first
 * page they do hold — the same rule the inventory app applies on sign-in, so
 * a warehouse account granted only the count sheet is not met by a dead
 * landing page.
 */
function IndexRoute() {
  const { can } = useAuth();
  if (can(VIEW_PERMISSIONS.overview)) return <WorkOverviewPage />;
  const first = firstAccessiblePath(can);
  if (first) return <Navigate to={first} replace />;
  return <NoAccess hint="No Orders pages are granted to this account. Ask an administrator for the pages you need." />;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      {/* Print-only: no shell, no nav — the page IS the sheet. */}
      <Route
        path="/print/count-sheet/:receipt"
        element={
          <RequireAuth>
            <RequirePermission codes={viewCodes('print_count_sheet')}>
              <PrintCountSheetPage />
            </RequirePermission>
          </RequireAuth>
        }
      />

      <Route
        element={
          <RequireAuth>
            <RequirePermission codes={viewCodes('shell')}>
              <AppShell />
            </RequirePermission>
          </RequireAuth>
        }
      >
        <Route index element={<IndexRoute />} />

        <Route
          path={routePath('today')}
          element={
            <RequirePermission codes={viewCodes('today')}>
              <TodayPage />
            </RequirePermission>
          }
        />

        <Route
          path={routePath('orders')}
          element={
            <RequirePermission codes={viewCodes('orders')}>
              <JourneysPage />
            </RequirePermission>
          }
        />
        <Route
          path={`${routePath('orders')}/:id`}
          element={
            <RequirePermission codes={viewCodes('orders')}>
              <OrderWorkspacePage />
            </RequirePermission>
          }
        />

        <Route path={routePath('actions')} element={workRoute('actions')} />
        <Route path={routePath('to_order')} element={workRoute('to_order')} />
        <Route path={routePath('balance')} element={workRoute('balance')} />
        <Route path={routePath('decide')} element={workRoute('decide')} />
        <Route path={routePath('move')} element={workRoute('move')} />
        <Route path={routePath('prices')} element={workRoute('prices')} />
        <Route path={routePath('duties')} element={workRoute('duties')} />
        <Route path={routePath('followups')} element={workRoute('followups')} />
        <Route path={routePath('releases')} element={workRoute('releases')} />
        <Route path={routePath('documents')} element={workRoute('documents')} />

        <Route path={routePath('setup')} element={workRoute('setup')} />
        <Route path={`${routePath('setup')}/:folder`} element={workRoute('setup')} />

        <Route
          path={routePath('packages')}
          element={
            <RequirePermission codes={viewCodes('packages')}>
              <PackagesPage />
            </RequirePermission>
          }
        />

        <Route
          path={routePath('warehouse')}
          element={
            <RequirePermission codes={viewCodes('warehouse')}>
              <WarehousePage />
            </RequirePermission>
          }
        />

        {/* The shell's base read lists them; Block/Remove carry their own write code. */}
        <Route path={FETCH_DOMAINS_PATH.slice(1)} element={<FetchDomainsPage />} />

        <Route path="no-access" element={<NoAccess />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
